import { Check, X } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { SHORTLIST_COMMENT_MAX, SHORTLIST_REASON_MAX, type ShortlistCandidate, type ShortlistReasonOption } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { Select } from '../../../components/Select';
import type { AnswerChoice } from '../api/shortlist-api';
import styles from './shortlist.module.css';

const NO_CODE = '';

interface AnswerControlsProps {
  readonly candidate: ShortlistCandidate;
  readonly reasons: readonly ShortlistReasonOption[];
  readonly isPending: boolean;
  readonly isDisabled?: boolean;
  readonly onAnswer: (choice: AnswerChoice) => Promise<boolean>;
  /** D5: a No typed but not saved yet, or null once it is saved or put away — so Done never loses it. */
  readonly onDraft?: (draft: AnswerChoice | null) => void;
}

/**
 * F24.02 on one person (§L31, design card "Review and shortlist"): two large pills, Yes, interview
 * and No. A No asks for a short reason — Save stays disabled until there is one — and may carry a
 * §L19 code; a comment goes with either. Used on SHL-01's card, and in SHL-02's "Change answer".
 */
export function AnswerControls({ candidate, reasons, isPending, isDisabled = false, onAnswer, onDraft }: AnswerControlsProps): ReactNode {
  const [isSayingNo, setIsSayingNo] = useState(false);
  const [reason, setReason] = useState(candidate.reason ?? '');
  const [reasonCode, setReasonCode] = useState(candidate.reasonCode ?? NO_CODE);
  const [comment, setComment] = useState(candidate.comment ?? '');
  const commentId = useId();
  const isAnswered = candidate.answer === 'yes' || candidate.answer === 'no';
  const isLocked = isDisabled || isPending;

  const noChoice = (text: string, code: string): AnswerChoice => ({ answer: 'no', reason: text.trim(), reasonCode: code === NO_CODE ? null : code, comment: comment.trim() === '' ? null : comment.trim() });

  /** What the open No form holds, told to the page when it changes — nothing when it matches the saved No. */
  function reportDraft(isOpen: boolean, text: string, code: string): void {
    const isSavedAlready = candidate.answer === 'no' && text.trim() === (candidate.reason ?? '') && code === (candidate.reasonCode ?? NO_CODE);
    onDraft?.(isOpen && text.trim() !== '' && !isSavedAlready ? noChoice(text, code) : null);
  }

  function setNoOpen(isOpen: boolean): void {
    setIsSayingNo(isOpen);
    reportDraft(isOpen, reason, reasonCode);
  }

  async function sayYes(): Promise<void> {
    setNoOpen(false);
    await onAnswer({ answer: 'yes', comment: comment.trim() === '' ? null : comment.trim() });
  }

  async function sayNo(): Promise<void> {
    if (reason.trim() === '') return;
    const isSaved = await onAnswer(noChoice(reason, reasonCode));
    if (!isSaved) return;
    setIsSayingNo(false);
    onDraft?.(null);
  }

  /** A comment changed after answering is saved with the same answer. */
  function saveComment(): void {
    if (!isAnswered || (comment.trim() || null) === (candidate.comment ?? null)) return;
    void onAnswer(
      candidate.answer === 'no'
        ? { answer: 'no', reason: candidate.reason, reasonCode: candidate.reasonCode, comment: comment.trim() === '' ? null : comment.trim() }
        : { answer: 'yes', comment: comment.trim() === '' ? null : comment.trim() },
    );
  }

  return (
    <div className={styles.answer}>
      <div className={styles.answerButtons} role="group" aria-label={`Your answer for ${candidate.candidate.fullName}`}>
        <Button
          variant={candidate.answer === 'yes' ? 'solid' : 'outline'}
          size="lg"
          icon={Check}
          aria-pressed={candidate.answer === 'yes'}
          disabled={isLocked}
          onClick={() => void sayYes()}
        >
          Yes, interview
        </Button>
        <Button
          variant={candidate.answer === 'no' && !isSayingNo ? 'dangerSolid' : 'outline'}
          size="lg"
          icon={X}
          aria-pressed={candidate.answer === 'no'}
          aria-expanded={isSayingNo}
          disabled={isLocked}
          onClick={() => setNoOpen(!isSayingNo)}
        >
          No
        </Button>
      </div>

      {isSayingNo && (
        <div className={styles.noForm}>
          <Field label="Why not?" isRequired hint="A few words for HR. The candidate never sees this.">
            {(ids) => (
              <Input
                id={ids.inputId}
                aria-describedby={ids.describedBy}
                value={reason}
                maxLength={SHORTLIST_REASON_MAX}
                placeholder="Too junior for this team"
                onChange={(event) => {
                  setReason(event.target.value);
                  reportDraft(true, event.target.value, reasonCode);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void sayNo();
                }}
                autoFocus
              />
            )}
          </Field>
          {reasons.length > 0 && (
            <Field label="Reason from the list" isOptional>
              {(ids) => (
                <Select
                  id={ids.inputId}
                  label="Reason from the list"
                  placeholder="None"
                  value={reasonCode === NO_CODE ? null : reasonCode}
                  options={[{ value: NO_CODE, label: 'None' }, ...reasons.map((option) => ({ value: option.code, label: option.label }))]}
                  onChange={(code) => {
                    setReasonCode(code);
                    reportDraft(true, reason, code);
                  }}
                />
              )}
            </Field>
          )}
          <div className={styles.noActions}>
            <Button variant="ghost" onClick={() => setNoOpen(false)}>
              Cancel
            </Button>
            <Button variant="dangerSolid" disabled={reason.trim() === '' || isLocked} onClick={() => void sayNo()}>
              Save No
            </Button>
          </div>
        </div>
      )}

      <div className={styles.comment}>
        <label htmlFor={commentId} className={styles.commentLabel}>
          Comment <span className={styles.optional}>optional</span>
        </label>
        <Textarea id={commentId} rows={2} value={comment} maxLength={SHORTLIST_COMMENT_MAX} disabled={isLocked} placeholder="Anything HR should know before the interview" onChange={(event) => setComment(event.target.value)} onBlur={saveComment} />
      </div>
    </div>
  );
}
